import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  Button,
} from '@fluentui/react-components';
import {
  MoneyRegular,
  CartRegular,
  DocumentRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../contexts/AuthContext';
import '../styles/DomainSelectionPage.css';

interface DomainConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  connectors: string[];
  enabled: boolean;
  metrics?: { label: string; value: string }[];
  ctaLabel?: string;
  route?: string;
}

const domains: DomainConfig[] = [
  {
    id: 'ap-invoice-dashboard-demo',
    name: 'AP Exception Resolution',
    description: 'Resolve invoice mismatches, PO lookups, vendor inquiries, and payment holds across ERP, email, and documents.',
    icon: <DocumentRegular style={{ fontSize: '48px' }} />,
    color: '#5C2D91',
    connectors: ['ERP', 'Email', 'Documents', 'Approvals', 'Audit Trail'],
    enabled: true,
    metrics: [
      { label: 'Open invoices', value: '142' },
      { label: 'Blocked value', value: '$4.2M' },
      { label: 'Avg resolution', value: '2.1d' },
    ],
    ctaLabel: 'Open AP Dashboard',
    route: '/ap-invoice-dashboard',
  },
  {
    id: 'finance',
    name: 'Finance Exceptions',
    description: 'Resolve close, reconciliation, and variance exceptions with approval gates and ERP writeback.',
    icon: <MoneyRegular style={{ fontSize: '48px' }} />,
    color: '#107C10',
    connectors: ['ERP', 'Rossum', 'Bill.com', 'Approvals'],
    enabled: true,
    metrics: [
      { label: 'Unresolved variances', value: '67' },
      { label: 'Pending approvals', value: '$8.4M' },
      { label: 'Close exceptions', value: '12' },
    ],
    ctaLabel: 'Open Finance Workspace',
    route: '/workspace',
  },
  {
    id: 'order-management',
    name: 'Revenue Exceptions',
    description: 'Resolve billing discrepancies, order holds, and revenue blockers across CRM and ERP.',
    icon: <CartRegular style={{ fontSize: '48px' }} />,
    color: '#0078D4',
    connectors: ['NetSuite', 'Salesforce', 'Email', 'Approvals'],
    enabled: true,
    metrics: [
      { label: 'Revenue at risk', value: '$11.3M' },
      { label: 'Order holds', value: '38' },
      { label: 'Billing gaps', value: '14' },
    ],
    ctaLabel: 'Open Revenue Workspace',
    route: '/workspace',
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain Exceptions',
    description: 'Resolve PO blockers, receipt mismatches, and supplier inquiries across systems.',
    icon: <CartRegular style={{ fontSize: '48px' }} />,
    color: '#8764B8',
    connectors: ['ERP', 'Documents', 'Email', 'Approvals'],
    enabled: true,
    metrics: [
      { label: 'Active exceptions', value: '48' },
      { label: 'SLA exposure', value: '$3.7M' },
      { label: 'Supplier delays', value: '19' },
    ],
    ctaLabel: 'Open Supply Chain Center',
    route: '/domains/supply-chain',
  },
  {
    id: 'vas',
    name: 'VAS / Merchant Services',
    description: 'Operationalize VIK, incentives, rebates, credits, and partner obligations.',
    icon: <MoneyRegular style={{ fontSize: '48px' }} />,
    color: '#5C2D91',
    connectors: ['Dynamics CRM', 'Dynamics F&O', 'Contracts', 'Policy Docs', 'Approvals'],
    enabled: true,
    metrics: [
      { label: 'Active commitments', value: '$186M' },
      { label: 'At-risk next 30 days', value: '$14.8M' },
      { label: 'Open exceptions', value: '23' },
    ],
    ctaLabel: 'Open VAS Command Center',
    route: '/domains/vas',
  },
];

const DomainSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleDomainSelect = (domain: DomainConfig) => {
    if (!domain.enabled) return;

    // Store selected domain in sessionStorage for workspace-bound domains
    if (domain.route === '/workspace') {
      sessionStorage.setItem('selectedDomain', domain.id);
      sessionStorage.setItem('selectedDomainName', domain.name);
    }

    if (domain.route) {
      navigate(domain.route);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="domain-selection-container">
      <div className="domain-selection-header">
        <div className="header-left">
          <Text size={800} weight="bold" className="header-title">
            Choose an operational workflow
          </Text>
          <Text size={400} className="header-subtitle">
            Start with AP exceptions. Expand across finance, revenue, and supply chain.
          </Text>
        </div>
        <div className="header-right">
          <Text size={300} className="welcome-text">
            Welcome, {user?.displayName || user?.username || 'User'}
          </Text>
          <Button appearance="subtle" onClick={handleLogout}>
            Sign Out
          </Button>
        </div>
      </div>

      <div className="domains-grid">
        {domains.map((domain) => (
          <Card
            key={domain.id}
            className="domain-card"
            onClick={() => handleDomainSelect(domain)}
          >
            <div className="domain-card-content">
              <div 
                className="domain-icon" 
                style={{ color: domain.color }}
              >
                {domain.icon}
              </div>
              
              <div className="domain-info">
                <div className="domain-title-row">
                  <Text size={500} weight="semibold" className="domain-name">
                    {domain.name}
                  </Text>
                </div>
                
                <Text size={300} className="domain-description">
                  {domain.description}
                </Text>

                {domain.metrics && (
                  <div className="domain-metrics">
                    {domain.metrics.map((metric, idx) => (
                      <div key={idx} className="domain-metric-item">
                        <Text size={200} className="domain-metric-value" style={{ color: domain.color }}>{metric.value}</Text>
                        <Text size={200} className="domain-metric-label">{metric.label}</Text>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="domain-connectors">
                  <Text size={200} className="connectors-label">
                    Integrations:
                  </Text>
                  <div className="connectors-list">
                    {domain.connectors.slice(0, 5).map((connector, idx) => (
                      <span key={idx} className="connector-tag">
                        {connector}
                      </span>
                    ))}
                    {domain.connectors.length > 5 && (
                      <span className="connector-tag connector-more">
                        +{domain.connectors.length - 5}
                      </span>
                    )}
                  </div>
                </div>

                {domain.ctaLabel && (
                  <div className="domain-cta">
                    <Text size={200} weight="semibold" className="domain-cta-label" style={{ color: domain.color }}>
                      {domain.ctaLabel} →
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="domain-selection-footer">
        <Text size={200} className="footer-text">
          Powered by Nolij Decision Loop • Detect · Investigate · Coordinate · Execute · Learn
        </Text>
      </div>
    </div>
  );
};

export default DomainSelectionPage;
